<script setup lang="ts">
import AddNaturalPerson from '@/components/dialogs/AddNaturalPerson.vue'
import InertiaFormField from '@/components/InertiaFormField.vue'
import CompaniesController from '@controllers/Investors/CompaniesController'
import ProfileController from '@controllers/Investors/ProfileController'
import UserManagementController from '@controllers/Investors/UserManagementController'
import NaturalPersonController from '@controllers/NaturalPersonController'
import InvestorInvitationController from '@controllers/InvestorInvitationController'
import { router, useForm } from '@inertiajs/vue3'
import { useConfirm } from 'primevue/useconfirm'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import NaturalPersonData = App.Data.User.NaturalPersonData
import NaturalPersonType = App.Enums.User.NaturalPersonType

const { t } = useI18n()

type SharedCompany = {
  id: string
  name: string
}

type SharedCompanyUser = {
  id: string
  name: string
  email: string
  companies: SharedCompany[]
}

type SharedCompanyPendingInvite = {
  id: string
  email: string
  companies: SharedCompany[]
}

type PendingInvitation = {
  id: string
  natural_person_id: string | null
  email: string
}

defineProps<{
  household: NaturalPersonData[]
  availableNaturalPersonTypes: NaturalPersonType[]
  canRemoveHouseholdAccess: boolean
  pendingInvitations: PendingInvitation[]
  sharedCompanyUsers: SharedCompanyUser[]
  sharedCompanyPendingInvites: SharedCompanyPendingInvite[]
}>()

const showEmailModal = ref(false)
const selectedPerson = ref<NaturalPersonData | null>(null)

const form = useForm({
  email: null,
  natural_person_id: null as string | null,
})

const showAddNaturalPerson = ref(false)

const confirm = useConfirm()

const clearForm = () => {
  form.reset()
  selectedPerson.value = null
}

const submitInvitationWithEmail = () => {
  if (!selectedPerson.value?.id || !form.email) {
    return
  }

  form.natural_person_id = selectedPerson.value.id

  form.post(InvestorInvitationController.store().url, {
    preserveScroll: true,
    onSuccess: () => {
      showEmailModal.value = false
      clearForm()
    },
    onError: () => {
      form.reset('email')
    },
  })
}

const invitePerson = (person: NaturalPersonData) => {
  if (!person.email) {
    selectedPerson.value = person
    showEmailModal.value = true
    return
  }

  confirm.require({
    header: t('confirmInvite.header'),
    message: t('confirmInvite.delete', { name: person.name }),
    rejectProps: {
      label: t('confirmInvite.reject'),
      severity: 'secondary',
      outlined: true,
    },
    acceptProps: {
      label: t('confirmInvite.accept'),
      severity: 'primary',
      icon: 'pi pi-send',
    },
    accept: () => {
      sendInvitation(person)
    },
  })
}

const deletePerson = (person: NaturalPersonData) => {
  confirm.require({
    header: t('confirmDelete.header'),
    message: t('confirmDelete.delete', { name: person.name }),
    rejectProps: {
      label: t('confirmDelete.reject'),
      severity: 'secondary',
      outlined: true,
    },
    acceptProps: {
      label: t('confirmDelete.accept'),
      severity: 'danger',
    },
    accept: () => {
      if (!person.id) return

      router.delete(NaturalPersonController.destroy(person.id).url, {
        preserveScroll: true,
      })
    },
  })
}

const revokeInvitation = (person: NaturalPersonData) => {
  if (!person.id) return

  router.delete(InvestorInvitationController.destroy(person.id).url, {
    preserveScroll: true,
  })
}

const removeHouseholdAccess = (person: NaturalPersonData) => {
  const investorAccountId = person.investor_account_id

  if (typeof investorAccountId !== 'string' || investorAccountId.length === 0) {
    return
  }

  confirm.require({
    header: t('confirmRemoveAccess.header'),
    message: t('confirmRemoveAccess.message', { name: person.name }),
    rejectProps: {
      label: t('confirmRemoveAccess.reject'),
      severity: 'secondary',
      outlined: true,
    },
    acceptProps: {
      label: t('confirmRemoveAccess.accept'),
      severity: 'danger',
    },
    accept: () => {
      router.delete(UserManagementController.destroyAccess(investorAccountId).url, {
        preserveScroll: true,
      })
    },
  })
}

const updateLivingInHousehold = (id: string, livingInHousehold: boolean) => {
  router.put(
    NaturalPersonController.update(id).url,
    { living_in_household: livingInHousehold },
    { preserveScroll: true },
  )
}

const sendInvitation = (person: NaturalPersonData) => {
  if (!person.id) return

  form.natural_person_id = person.id
  form.email = null

  form.post(InvestorInvitationController.store().url, {
    onSuccess: () => {
      showEmailModal.value = false
      clearForm()
    },
    preserveScroll: true,
  })
}

const companyHref = (companyId: string): string => {
  return CompaniesController.show(companyId).url
}

const normalizeEmail = (email?: string | null): string | null => {
  if (!email) {
    return null
  }

  return email.trim().toLowerCase()
}

const hasPendingInvitation = (person: NaturalPersonData, pendingInvitations: PendingInvitation[]): boolean => {
  if (person.investor_account_id) {
    return false
  }

  const normalizedEmail = normalizeEmail(person.email)

  return pendingInvitations.some((invitation) => {
    if (person.id && invitation.natural_person_id === person.id) {
      return true
    }

    if (invitation.natural_person_id) {
      return false
    }

    return normalizedEmail !== null && invitation.email === normalizedEmail
  })
}
</script>

<template>
  <Dialog
    v-model:visible="showEmailModal"
    modal
    :header="t('missingEmail.title')"
    class="w-full max-w-md"
    @after-hide="clearForm">
    <div class="text-surface-500 mb-8">
      {{ t('missingEmail.message', { name: selectedPerson?.name }) }}
    </div>
    <div class="mb-8 flex items-center gap-4">
      <InertiaFormField :label="t('missingEmail.label')" :error="form.errors.email" class="w-full">
        <InputGroupAddon>
          <i class="pi pi-envelope" />
        </InputGroupAddon>
        <InputText v-model="form.email" type="email" fluid required />
      </InertiaFormField>
    </div>
    <div class="flex justify-end gap-2">
      <Button type="button" :label="t('missingEmail.cancel')" severity="secondary" @click="showEmailModal = false" />
      <Button type="button" :label="t('missingEmail.save')" @click="submitInvitationWithEmail" />
    </div>
  </Dialog>

  <div>
    <AddNaturalPerson v-model:visible="showAddNaturalPerson" :available-types="availableNaturalPersonTypes" />
    <Toolbar>
      <template #end>
        <div class="flex gap-2">
          <Button
            :label="t('toolbar.add-person')"
            icon="pi pi-user-plus"
            @click="showAddNaturalPerson = !showAddNaturalPerson" />
        </div>
      </template>
    </Toolbar>
  </div>

  <Card>
    <template #title>{{ t('title') }}</template>
    <template #subtitle>{{ t('subtitle') }}</template>
    <template #content>
      <DataTable :value="household">
        <Column field="name" :header="t('household.name')" />
        <Column field="type" :header="t('household.type')">
          <template #body="{ data }">
            <span>{{ t(`type.${data.type}`) }}</span>
          </template>
        </Column>
        <Column field="living_in_household" :header="t('household.living_in_household')">
          <template #body="{ data }">
            <Checkbox
              v-model="data.living_in_household"
              :disabled="data.is_main_person"
              binary
              @update:model-value="data.id && updateLivingInHousehold(data.id, data.living_in_household)" />
          </template>
        </Column>
        <Column>
          <template #body>
            <div class="flex gap-2">
              <Link :href="ProfileController()">
                <Button :label="t('household.personal_details')" size="small" />
              </Link>
            </div>
          </template>
        </Column>
        <Column field="name" :header="t('household.access')">
          <template #body="{ data }">
            <div class="flex items-center gap-2">
              <Badge v-if="data.investor_account_id" severity="success" class="inline-flex gap-2">
                <i class="pi pi-check" />
                {{ t('access.full') }}
              </Badge>
              <Button
                v-if="data.investor_account_id && canRemoveHouseholdAccess && !data.is_main_person"
                icon="pi pi-user-minus"
                size="small"
                severity="danger"
                outlined
                :label="t('access.remove')"
                @click="removeHouseholdAccess(data)" />
              <Button
                v-if="!data.investor_account_id && !hasPendingInvitation(data, pendingInvitations)"
                icon="pi pi-envelope"
                size="small"
                severity="secondary"
                :label="t('access.invite')"
                @click="invitePerson(data)" />
              <Button
                v-if="hasPendingInvitation(data, pendingInvitations)"
                icon="pi pi-clock"
                size="small"
                severity="secondary"
                disabled
                :label="t('access.pending')" />
              <Button
                v-if="hasPendingInvitation(data, pendingInvitations)"
                icon="pi pi-ban"
                size="small"
                severity="secondary"
                :label="t('access.revoke')"
                @click="revokeInvitation(data)" />
              <Button
                v-if="!data.investor_account_id && !hasPendingInvitation(data, pendingInvitations)"
                icon="pi pi-trash"
                size="small"
                severity="secondary"
                :label="t('access.delete')"
                @click="deletePerson(data)" />
            </div>
          </template>
        </Column>
      </DataTable>
    </template>
  </Card>

  <Card class="mt-6">
    <template #title>{{ t('sharedAccess.title') }}</template>
    <template #subtitle>{{ t('sharedAccess.subtitle') }}</template>
    <template #content>
      <DataTable v-if="sharedCompanyUsers.length > 0" :value="sharedCompanyUsers">
        <Column field="name" :header="t('sharedAccess.columns.name')" />
        <Column field="email" :header="t('sharedAccess.columns.email')" />
        <Column :header="t('sharedAccess.columns.companies')">
          <template #body="{ data }">
            <div class="flex flex-wrap gap-2">
              <Link v-for="company in data.companies" :key="company.id" :href="companyHref(company.id)" tabindex="-1">
                <Tag :value="company.name" severity="info" class="cursor-pointer" />
              </Link>
            </div>
          </template>
        </Column>
      </DataTable>
      <Message v-else severity="info">
        {{ t('sharedAccess.empty') }}
      </Message>

      <div v-if="sharedCompanyPendingInvites.length > 0" class="mt-6">
        <h4 class="text-base font-semibold">
          {{ t('sharedAccess.pending.title') }}
        </h4>
        <p class="text-surface-600 mt-1 mb-3 text-sm">
          {{ t('sharedAccess.pending.subtitle') }}
        </p>

        <DataTable :value="sharedCompanyPendingInvites">
          <Column field="email" :header="t('sharedAccess.pending.columns.email')" />
          <Column :header="t('sharedAccess.pending.columns.companies')">
            <template #body="{ data }">
              <div class="flex flex-wrap gap-2">
                <Link v-for="company in data.companies" :key="company.id" :href="companyHref(company.id)" tabindex="-1">
                  <Tag :value="company.name" severity="info" class="cursor-pointer" />
                </Link>
              </div>
            </template>
          </Column>
        </DataTable>
      </div>
    </template>
  </Card>
</template>

<i18n>
{
  "de": {
    "title": "Benutzerverwaltung",
    "subtitle": "Verwalten Sie die Benutzer, die Zugriff auf Ihr Konto haben.",
    "toolbar": {
      "add-person": "Person hinzufügen"
    },
    "household": {
      "name": "Name",
      "type": "Typ",
      "living_in_household": "Lebt im Haushalt",
      "personal_details": "Persönliche Daten",
      "access": "Zugriff"
    },
    "type": {
      "primary": "Hauptperson",
      "partner": "Partner",
      "child": "Kind",
      "other": "Andere"
    },
    "access": {
      "full": "Voller Zugriff",
      "remove": "Zugriff entziehen",
      "invite": "Einladen",
      "pending": "Ausstehend",
      "revoke": "Widerrufen",
      "delete": "Löschen"
    },
    "confirmInvite": {
      "header": "Einladung senden",
      "delete": "Möchten Sie wirklich eine Einladung an {name} senden?",
      "accept": "Einladen",
      "reject": "Abbrechen"
    },
    "confirmDelete": {
      "header": "Person löschen",
      "delete": "Möchten Sie {name} wirklich löschen?",
      "accept": "Löschen",
      "reject": "Abbrechen"
    },
    "confirmRemoveAccess": {
      "header": "Zugriff entziehen",
      "message": "Möchtest du den Portalzugriff für {name} wirklich entziehen?",
      "accept": "Zugriff entziehen",
      "reject": "Abbrechen"
    },
    "missingEmail": {
      "title": "E-Mail-Adresse",
      "message": "Für {name} wird eine E-Mail-Adresse benötigt, um eine Einladung zu senden.",
      "label": "E-Mail-Adresse",
      "cancel": "Abbrechen",
      "save": "Speichern"
    },
    "sharedAccess": {
      "title": "Geteilte Unternehmenszugriffe",
      "subtitle": "Hier siehst du alle Benutzer, mit denen du über Unternehmen Zugriff teilst. Diese Tabelle ist nur zur Ansicht.",
      "columns": {
        "name": "Benutzer",
        "email": "E-Mail",
        "companies": "Gemeinsame Unternehmen"
      },
      "pending": {
        "title": "Ausstehende Einladungen",
        "subtitle": "Diese Einladungen sind noch nicht angenommen.",
        "columns": {
          "email": "E-Mail",
          "companies": "Unternehmen"
        }
      },
      "empty": "Aktuell teilst du über Unternehmen keinen Zugriff mit anderen Benutzern."
    }
  },
  "en": {
    "title": "User Management",
    "subtitle": "Manage the users who have access to your account.",
    "toolbar": {
      "add-person": "Add person"
    },
    "household": {
      "name": "Name",
      "type": "Type",
      "living_in_household": "Living in Household",
      "personal_details": "Personal Details",
      "access": "Access"
    },
    "type": {
      "primary": "Main Person",
      "partner": "Partner",
      "child": "Child",
      "other": "Other"
    },
    "access": {
      "full": "Full Access",
      "remove": "Remove access",
      "invite": "Invite",
      "pending": "Pending",
      "revoke": "Revoke",
      "delete": "Delete"
    },
    "confirmInvite": {
      "header": "Send Invitation",
      "delete": "Are you sure you want to send an invitation to {name}?",
      "accept": "Invite",
      "reject": "Cancel"
    },
    "confirmDelete": {
      "header": "Delete person",
      "delete": "Do you really want to delete {name}?",
      "accept": "Delete",
      "reject": "Cancel"
    },
    "confirmRemoveAccess": {
      "header": "Remove access",
      "message": "Do you really want to remove portal access for {name}?",
      "accept": "Remove access",
      "reject": "Cancel"
    },
    "missingEmail": {
      "title": "Email address",
      "message": "An email address is required for {name} before sending an invitation.",
      "label": "Email address",
      "cancel": "Cancel",
      "save": "Save"
    },
    "sharedAccess": {
      "title": "Shared company access",
      "subtitle": "This table shows all users you share company access with. This table is read-only.",
      "columns": {
        "name": "User",
        "email": "Email",
        "companies": "Shared companies"
      },
      "pending": {
        "title": "Pending invitations",
        "subtitle": "These invitations have not been accepted yet.",
        "columns": {
          "email": "Email",
          "companies": "Companies"
        }
      },
      "empty": "You currently do not share company access with other users."
    }
  }
}
</i18n>
